import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router';
import {
  acquire,
  getCandidates,
  getEntitlement,
  getJob,
  getVods,
  isDailyCapacityReachedError,
  isGenerationCeilingError,
  isGenerationPausedError,
  isMonthlyCapReachedError,
  isNotAllowlistedError,
  isReelAlreadyExistsError,
  isReelNameRequiredError,
  isTwitchSignInRequiredError,
  isUnsupportedGameError,
  isUpgradeRequiredError,
  submitCurate,
} from '../api/rttsClient';
import type { CurationCandidate, Entitlement, HighlightJob, ReelDurationTier } from '../api/types';
import { beginTwitchConnect } from '../auth/beginTwitchConnect';
import { setMockAuthState } from '../api/mockState';
import CapReachedCard from '../components/CapReachedCard';
import CurationReviewPanel from '../components/CurationReviewPanel';
import DurationTierPicker from '../components/DurationTierPicker';
import ReelNameField from '../components/ReelNameField';
import ReconnectTwitchCard from '../components/ReconnectTwitchCard';
import ReelResultPanel from '../components/ReelResultPanel';
import StudioProgressPanel from '../components/StudioProgressPanel';
import { STUDIO_COPY, STUDIO_DOCUMENT_TITLES, formatGenerateFailedCopy, isNoCandidatesError, isStudioBillingEnabled, isStudioUseMock } from '../config';
import { useJobPolling } from '../hooks/useJobPolling';
import { useStudioAuth } from '../hooks/useStudioAuth';
import { useStudioDocumentTitle } from '../hooks/useStudioDocumentTitle';
import { isGameEligibleVod, isReelEligibleVod, isUnlimitedEntitlement } from '../utils/vodEligibility';
import { isReelNameValid, normalizeReelName } from '../utils/reelName';
import { formatResetDate } from '../utils/format';

type FlowState =
  | { kind: 'checking_entitlement' }
  | { kind: 'acquiring' }
  | { kind: 'polling'; jobId: string; vodDurationSec?: number | null }
  | { kind: 'reconnect' }
  | { kind: 'too_short' }
  | { kind: 'unsupported_game' }
  | {
      kind: 'cap_reached';
      variant?: 'free' | 'paid';
      periodCap?: number;
      periodResetsAt?: string;
    }
  | { kind: 'daily_capacity'; periodResetsAt?: string }
  | { kind: 'not_allowlisted' }
  | { kind: 'generation_paused' }
  | { kind: 'already_exists'; job: HighlightJob; entitlement: Entitlement | null }
  | { kind: 'already_exists_missing_id' }
  | { kind: 'failed'; job: HighlightJob; entitlement: Entitlement | null }
  | { kind: 'error'; message: string };

type AcquireOutcome =
  | { kind: 'polling'; jobId: string; entitlement: Entitlement; vodDurationSec?: number | null }
  | { kind: 'upgrade' }
  | { kind: 'reconnect' }
  | { kind: 'too_short' }
  | { kind: 'unsupported_game' }
  | {
      kind: 'cap_reached';
      variant?: 'free' | 'paid';
      periodCap?: number;
      periodResetsAt?: string;
    }
  | { kind: 'daily_capacity'; periodResetsAt?: string }
  | { kind: 'not_allowlisted' }
  | { kind: 'generation_paused' }
  | { kind: 'already_exists'; job: HighlightJob; entitlement: Entitlement | null }
  | { kind: 'already_exists_missing_id' }
  | { kind: 'error'; message: string };

/** One in-flight acquire per (vodId, attempt) — survives StrictMode remount / double-submit. */
const acquirePromises = new Map<string, Promise<AcquireOutcome>>();

/** Test helper — clear acquire locks between tests. */
export function resetAcquireStartedKeys(): void {
  acquirePromises.clear();
}

/**
 * Acquire + route outcomes (live `/api/rtts/highlights/acquire` contract):
 * 200 {job} → poll; 402 upgrade_required → paywall; 402 monthly_cap_reached → CapReached;
 * 409 + job_id → existing reel; 401 Twitch → reconnect; admin/unlimited never pre-blocked.
 */
async function performAcquire(
  vodId: string,
  reelName: string,
  durationTier?: string
): Promise<AcquireOutcome> {
  try {
    const [vods, ent] = await Promise.all([getVods(), getEntitlement()]);
    const vod = vods.find((row) => row.vod_id === vodId);
    if (vod && !isGameEligibleVod(vod)) {
      return { kind: 'unsupported_game' };
    }
    if (vod && !isReelEligibleVod(vod)) {
      return { kind: 'too_short' };
    }
    // Admin / unlimited never blocked.
    // Legacy: free with reels_remaining 0 → upgrade before acquire.
    // Signoff: generate is gated by generation ceiling on the server (not accepted cap).
    if (
      !ent.signoff_billing_enabled &&
      !isUnlimitedEntitlement(ent) &&
      ent.tier === 'free' &&
      typeof ent.reels_remaining === 'number' &&
      ent.reels_remaining <= 0 &&
      (ent.reel_credits ?? 0) <= 0
    ) {
      return { kind: 'upgrade' };
    }
    const { job } = await acquire(vodId, { reelName, durationTier });
    return {
      kind: 'polling',
      jobId: job.id,
      entitlement: ent,
      vodDurationSec: vod?.duration_sec ?? job.source_duration_sec ?? null,
    };
  } catch (error) {
    if (isTwitchSignInRequiredError(error)) {
      if (isStudioUseMock()) {
        setMockAuthState('stale_link');
      }
      return { kind: 'reconnect' };
    }
    if (isUnsupportedGameError(error)) {
      return { kind: 'unsupported_game' };
    }
    if (isUpgradeRequiredError(error)) {
      return { kind: 'upgrade' };
    }
    if (isDailyCapacityReachedError(error)) {
      return { kind: 'daily_capacity', periodResetsAt: error.period_resets_at };
    }
    if (isNotAllowlistedError(error)) {
      return { kind: 'not_allowlisted' };
    }
    if (isGenerationPausedError(error)) {
      return { kind: 'generation_paused' };
    }
    if (isGenerationCeilingError(error)) {
      return {
        kind: 'cap_reached',
        periodCap: error.period_cap,
        periodResetsAt: error.period_resets_at,
      };
    }
    if (isMonthlyCapReachedError(error)) {
      return {
        kind: 'cap_reached',
        variant: 'paid',
        periodCap: error.period_cap,
        periodResetsAt: error.period_resets_at,
      };
    }
    if (isReelAlreadyExistsError(error)) {
      if (!error.job_id) {
        return { kind: 'already_exists_missing_id' };
      }
      try {
        const { job } = await getJob(error.job_id);
        let ent: Entitlement | null = null;
        try {
          ent = await getEntitlement();
        } catch {
          // keep null
        }
        return { kind: 'already_exists', job, entitlement: ent };
      } catch (fetchErr) {
        if (isTwitchSignInRequiredError(fetchErr)) {
          return { kind: 'reconnect' };
        }
        return { kind: 'already_exists_missing_id' };
      }
    }
    return {
      kind: 'error',
      message: 'Could not start your reel. Please try again.',
    };
  }
}

function getOrStartAcquire(
  key: string,
  vodId: string,
  reelName: string,
  durationTier?: string
): Promise<AcquireOutcome> {
  const existing = acquirePromises.get(key);
  if (existing) return existing;
  const promise = performAcquire(vodId, reelName, durationTier);
  acquirePromises.set(key, promise);
  return promise;
}

/**
 * Generate flow: entitlement check → acquire → poll → navigate to result.
 * Acquire fires exactly once per (vodId, attempt).
 */
const StudioGeneratePage: React.FC = () => {
  useStudioDocumentTitle(STUDIO_DOCUMENT_TITLES.generate);
  const { vodId } = useParams<{ vodId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const passedRaw = normalizeReelName(
    (location.state as { reelName?: string; durationTier?: ReelDurationTier } | null)
      ?.reelName
  );
  const passedName = isReelNameValid(passedRaw) ? passedRaw : '';
  const passedTierRaw = (
    location.state as { durationTier?: ReelDurationTier } | null
  )?.durationTier;
  const passedTier: ReelDurationTier =
    passedTierRaw === 'short' || passedTierRaw === 'long' ? passedTierRaw : 'mid';
  const { status, isLoggedOut, setMockState, refreshMock } = useStudioAuth();
  const [reelName, setReelName] = useState(passedName);
  const [dispatchName, setDispatchName] = useState(
    isReelNameValid(passedName) ? passedName : ''
  );
  const [durationTier, setDurationTier] = useState<ReelDurationTier>(passedTier);
  const [nameTouched, setNameTouched] = useState(false);
  const [flow, setFlow] = useState<FlowState>({ kind: 'checking_entitlement' });
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [pollResumeKey, setPollResumeKey] = useState(0);
  const [candidates, setCandidates] = useState<CurationCandidate[] | null>(null);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);
  const [curateSubmitting, setCurateSubmitting] = useState(false);
  const [curateError, setCurateError] = useState<string | null>(null);
  const [curationDeadline, setCurationDeadline] = useState<string | null>(null);
  const [curationSubmitted, setCurationSubmitted] = useState(false);
  const [sourcePreviewUrl, setSourcePreviewUrl] = useState<string | null>(null);
  const [sourcePreviewExpiresAt, setSourcePreviewExpiresAt] = useState<string | null>(
    null
  );
  const [refreshingPreview, setRefreshingPreview] = useState(false);
  const [recutBusy, setRecutBusy] = useState(false);
  const [recutError, setRecutError] = useState<string | null>(null);

  const pollingJobId = flow.kind === 'polling' ? flow.jobId : null;
  const pollState = useJobPolling(
    pollingJobId,
    flow.kind === 'polling',
    pollResumeKey
  );

  useEffect(() => {
    if (isLoggedOut || status === 'loading' || status === 'stale_link') return;
    void getEntitlement()
      .then((ent) => setEntitlement(ent))
      .catch(() => {
        // picker stays hidden
      });
  }, [isLoggedOut, status]);

  useEffect(() => {
    if (isLoggedOut || status === 'loading' || status === 'stale_link') return;
    if (!vodId) return;
    if (!isReelNameValid(dispatchName)) return;

    let cancelled = false;
    const key = `${vodId}:${attempt}:${dispatchName}:${durationTier}`;
    setCandidates(null);
    setCandidatesError(null);
    setCurateError(null);
    setCurationSubmitted(false);
    setSourcePreviewUrl(null);
    setSourcePreviewExpiresAt(null);
    setRecutError(null);
    setFlow({ kind: 'acquiring' });

    void getOrStartAcquire(key, vodId, dispatchName, durationTier).then((outcome) => {
      if (cancelled) return;
      switch (outcome.kind) {
        case 'polling':
          setEntitlement(outcome.entitlement);
          setFlow({
            kind: 'polling',
            jobId: outcome.jobId,
            vodDurationSec: outcome.vodDurationSec,
          });
          break;
        case 'upgrade':
          setFlow({ kind: 'cap_reached', variant: 'free' });
          break;
        case 'reconnect':
          acquirePromises.delete(key);
          refreshMock();
          setFlow({ kind: 'reconnect' });
          break;
        case 'too_short':
          acquirePromises.delete(key);
          setFlow({ kind: 'too_short' });
          break;
        case 'unsupported_game':
          acquirePromises.delete(key);
          setFlow({ kind: 'unsupported_game' });
          break;
        case 'cap_reached':
          setFlow({
            kind: 'cap_reached',
            variant: outcome.variant,
            periodCap: outcome.periodCap,
            periodResetsAt: outcome.periodResetsAt,
          });
          break;
        case 'daily_capacity':
          setFlow({
            kind: 'daily_capacity',
            periodResetsAt: outcome.periodResetsAt,
          });
          break;
        case 'not_allowlisted':
          setFlow({ kind: 'not_allowlisted' });
          break;
        case 'generation_paused':
          setFlow({ kind: 'generation_paused' });
          break;
        case 'already_exists':
          setEntitlement(outcome.entitlement);
          setFlow({
            kind: 'already_exists',
            job: outcome.job,
            entitlement: outcome.entitlement,
          });
          break;
        case 'already_exists_missing_id':
          setFlow({ kind: 'already_exists_missing_id' });
          break;
        case 'error':
          setFlow({ kind: 'error', message: outcome.message });
          break;
        default:
          break;
      }
    });

    return () => {
      cancelled = true;
    };
  }, [vodId, isLoggedOut, status, attempt, navigate, refreshMock, dispatchName, durationTier]);

  useEffect(() => {
    if (pollState.kind === 'delivered') {
      navigate(`/studio/reel/${encodeURIComponent(pollState.job.id)}`, {
        replace: true,
      });
    } else if (pollState.kind === 'failed') {
      setFlow({
        kind: 'failed',
        job: pollState.job,
        entitlement,
      });
    } else if (pollState.kind === 'reconnect') {
      setFlow({ kind: 'reconnect' });
    } else if (pollState.kind === 'error') {
      setFlow({ kind: 'error', message: pollState.message });
    }
  }, [pollState, navigate, entitlement]);

  useEffect(() => {
    if (pollState.kind !== 'awaiting_curation' || !pollingJobId || curationSubmitted) {
      return;
    }
    let cancelled = false;
    setCandidatesLoading(true);
    setCandidatesError(null);
    setCurationDeadline(pollState.job.curation_deadline_at ?? null);
    void getCandidates(pollingJobId)
      .then((payload) => {
        if (cancelled) return;
        setCandidates(payload.candidates);
        setCurationDeadline(
          payload.curation.deadline_at ?? pollState.job.curation_deadline_at ?? null
        );
        setSourcePreviewUrl(payload.source_preview_url ?? null);
        setSourcePreviewExpiresAt(payload.source_preview_expires_at ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setCandidatesError('Could not load plays to review. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setCandidatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pollState, pollingJobId, curationSubmitted]);

  const handleRefreshPreview = async () => {
    if (!pollingJobId) return;
    setRefreshingPreview(true);
    try {
      const payload = await getCandidates(pollingJobId);
      setCandidates(payload.candidates);
      setCurationDeadline(
        payload.curation.deadline_at ?? curationDeadline
      );
      setSourcePreviewUrl(payload.source_preview_url ?? null);
      setSourcePreviewExpiresAt(payload.source_preview_expires_at ?? null);
    } catch {
      setCurateError('Could not refresh preview. Please try again.');
    } finally {
      setRefreshingPreview(false);
    }
  };

  const handleCurateSubmit = async (selectedIds: string[], name: string) => {
    if (!pollingJobId || selectedIds.length === 0) return;
    const trimmed = normalizeReelName(name);
    if (!isReelNameValid(trimmed)) return;
    setCurateSubmitting(true);
    setCurateError(null);
    try {
      await submitCurate(pollingJobId, selectedIds, trimmed);
      setCurationSubmitted(true);
      setPollResumeKey((n) => n + 1);
    } catch {
      setCurateError('Could not start your cut. Please try again.');
    } finally {
      setCurateSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="flex justify-center py-16">
        <div className="studio-spinner" />
      </div>
    );
  }

  if (isLoggedOut) {
    return <Navigate to="/studio/connect" replace />;
  }

  if (status === 'stale_link' || flow.kind === 'reconnect') {
    return (
      <div data-testid="studio-generate-reconnect">
        <ReconnectTwitchCard
          onReconnect={() => {
            const shouldNavigate = beginTwitchConnect({
              returnTo: `/studio/generate/${vodId ?? ''}`,
            });
            if (shouldNavigate) {
              setMockState('linked');
              setAttempt((n) => n + 1);
            }
          }}
        />
      </div>
    );
  }

  if (!vodId) {
    return <Navigate to="/studio/vods" replace />;
  }

  if (!isReelNameValid(dispatchName)) {
    return (
      <div
        className="studio-panel space-y-4 max-w-xl mx-auto"
        data-testid="studio-generate-name"
      >
        <h1 className="studio-page-heading">{STUDIO_COPY.reelNameLabel}</h1>
        <ReelNameField
          value={reelName}
          onChange={setReelName}
          showError={nameTouched}
        />
        {entitlement?.duration_tiers_enabled ? (
          <DurationTierPicker
            value={durationTier}
            onChange={setDurationTier}
          />
        ) : null}
        <button
          type="button"
          className="studio-btn-primary"
          data-testid="studio-generate-cut-cta"
          disabled={!isReelNameValid(reelName)}
          onClick={() => {
            setNameTouched(true);
            if (!isReelNameValid(reelName)) return;
            setDispatchName(normalizeReelName(reelName));
          }}
        >
          {STUDIO_COPY.homeNextVodCta}
        </button>
      </div>
    );
  }

  if (flow.kind === 'too_short') {
    return (
      <div
        className="text-center space-y-4 py-10"
        data-testid="studio-generate-too-short"
        role="status"
      >
        <h1 className="text-2xl font-semibold">VOD too short</h1>
        <p className="text-[var(--studio-muted)]">{STUDIO_COPY.vodTooShortGenerate}</p>
        <Link to="/studio/vods" className="studio-btn-primary no-underline inline-flex">
          Back to VODs
        </Link>
      </div>
    );
  }

  if (flow.kind === 'unsupported_game') {
    return (
      <div
        className="text-center space-y-4 py-10"
        data-testid="studio-generate-unsupported-game"
        role="status"
      >
        <h1 className="text-2xl font-semibold">{STUDIO_COPY.vodsUnsupportedHeadline}</h1>
        <p className="text-[var(--studio-muted)]">{STUDIO_COPY.vodUnsupportedGame}</p>
        <Link to="/studio/vods" className="studio-btn-primary no-underline inline-flex">
          Back to VODs
        </Link>
      </div>
    );
  }

  if (flow.kind === 'cap_reached') {
    return (
      <CapReachedCard
        variant={flow.variant ?? (flow.periodCap ? 'paid' : 'free')}
        periodCap={flow.periodCap}
        periodResetsAt={flow.periodResetsAt}
      />
    );
  }

  if (flow.kind === 'daily_capacity') {
    const resetLabel = flow.periodResetsAt
      ? formatResetDate(flow.periodResetsAt)
      : 'tomorrow';
    return (
      <div
        className="studio-panel space-y-4"
        data-testid="studio-daily-capacity"
        role="status"
      >
        <div className="studio-tape-stripe rounded-sm opacity-70" aria-hidden />
        <h2 className="text-2xl font-semibold" data-testid="studio-daily-capacity-title">
          {STUDIO_COPY.dailyCapacityHeadline}
        </h2>
        <p className="studio-prose text-[var(--studio-muted)] leading-relaxed">
          {STUDIO_COPY.dailyCapacityBody} Resets {resetLabel}.
        </p>
        <Link to="/studio" className="studio-btn-primary no-underline inline-flex">
          Back to Studio
        </Link>
      </div>
    );
  }

  if (flow.kind === 'not_allowlisted') {
    return (
      <div
        className="studio-panel space-y-4"
        data-testid="studio-not-allowlisted"
        role="status"
      >
        <h2 className="text-2xl font-semibold">Invite only</h2>
        <p className="studio-prose text-[var(--studio-muted)] leading-relaxed">
          {STUDIO_COPY.notAllowlisted}
        </p>
      </div>
    );
  }

  if (flow.kind === 'generation_paused') {
    return (
      <div
        className="studio-panel space-y-4"
        data-testid="studio-generation-paused"
        role="status"
      >
        <h2 className="text-2xl font-semibold">Generation paused</h2>
        <p className="studio-prose text-[var(--studio-muted)] leading-relaxed">
          {STUDIO_COPY.generationPaused}
        </p>
      </div>
    );
  }

  if (flow.kind === 'already_exists') {
    return (
      <ReelResultPanel
        job={flow.job}
        entitlement={flow.entitlement}
        alreadyExists
        recutBusy={recutBusy}
        recutError={recutError}
        onRefineStarted={(nextJob) => {
          setFlow({ kind: 'polling', jobId: nextJob.id });
        }}
        onAcceptStarted={(nextJob) => {
          setFlow({ kind: 'polling', jobId: nextJob.id });
        }}
        onEntitlementRefresh={async () => {
          try {
            setEntitlement(await getEntitlement());
          } catch {
            // keep prior
          }
        }}
        onRecut={async (name, options) => {
          if (!vodId || recutBusy) return;
          setRecutBusy(true);
          setRecutError(null);
          try {
            const { job } = await acquire(vodId, {
              recut: true,
              reelName: name,
              force: Boolean(options?.force),
            });
            let ent = entitlement;
            try {
              ent = await getEntitlement();
              setEntitlement(ent);
            } catch {
              // keep prior entitlement
            }
            setFlow({ kind: 'polling', jobId: job.id });
          } catch (error) {
            if (isUpgradeRequiredError(error)) {
              if (isStudioBillingEnabled()) {
                navigate('/studio/upgrade', { replace: true });
              } else {
                setFlow({ kind: 'cap_reached' });
              }
              return;
            }
            if (isGenerationCeilingError(error)) {
              setRecutError('generation_ceiling_reached');
              return;
            }
            if (isMonthlyCapReachedError(error)) {
              setFlow({
                kind: 'cap_reached',
                periodCap: error.period_cap,
                periodResetsAt: error.period_resets_at,
              });
              return;
            }
            if (isDailyCapacityReachedError(error)) {
              setFlow({
                kind: 'daily_capacity',
                periodResetsAt: error.period_resets_at,
              });
              return;
            }
            if (isNotAllowlistedError(error)) {
              setFlow({ kind: 'not_allowlisted' });
              return;
            }
            if (isGenerationPausedError(error)) {
              setFlow({ kind: 'generation_paused' });
              return;
            }
            if (isTwitchSignInRequiredError(error)) {
              setFlow({ kind: 'reconnect' });
              return;
            }
            if (isReelNameRequiredError(error)) {
              setRecutError(STUDIO_COPY.reelNameRequired);
              return;
            }
            setRecutError('Could not start a re-cut. Please try again.');
          } finally {
            setRecutBusy(false);
          }
        }}
      />
    );
  }

  if (flow.kind === 'already_exists_missing_id') {
    return (
      <div
        className="text-center space-y-4 py-10"
        data-testid="studio-already-exists-no-job"
      >
        <p className="text-[var(--studio-muted)]">{STUDIO_COPY.alreadyExists}</p>
        <Link to="/studio/vods" className="studio-btn-ghost no-underline inline-flex">
          Back to VODs
        </Link>
      </div>
    );
  }

  if (flow.kind === 'failed') {
    const noPlays = isNoCandidatesError(flow.job.error_code);
    return (
      <div
        className="text-center space-y-5 py-10"
        data-testid="studio-generate-failed"
        data-error-code={flow.job.error_code || ''}
      >
        <h1 className="text-2xl font-semibold">
          {noPlays ? 'No highlights found' : 'Cut interrupted'}
        </h1>
        <p className="text-[var(--studio-muted)]">
          {formatGenerateFailedCopy(flow.job.error_code)}
        </p>
        <p
          className="text-xs text-[var(--studio-muted)]"
          data-testid="studio-failed-no-charge"
        >
          {STUDIO_COPY.failedCutNoChargeNote}
        </p>
        {noPlays ? (
          <Link
            to="/studio/vods"
            className="studio-btn-primary no-underline inline-flex"
            data-testid="studio-generate-try-another-vod"
          >
            {STUDIO_COPY.noCandidatesCta}
          </Link>
        ) : (
          <button
            type="button"
            className="studio-btn-primary"
            data-testid="studio-generate-retry"
            onClick={() => setAttempt((n) => n + 1)}
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (flow.kind === 'error') {
    return (
      <div className="text-center space-y-4 py-10" role="alert">
        <p>{flow.message}</p>
        <button
          type="button"
          className="studio-btn-ghost"
          onClick={() => {
            if (vodId) acquirePromises.delete(`${vodId}:${attempt}:${dispatchName}:${durationTier}`);
            setAttempt((n) => n + 1);
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (flow.kind === 'polling' && pollState.kind === 'awaiting_curation' && !curationSubmitted) {
    if (candidatesLoading || (!candidates && !candidatesError)) {
      return (
        <div
          className="flex flex-col items-center gap-4 py-16"
          data-testid="studio-curation-loading"
        >
          <div className="studio-spinner" />
          <p className="text-[var(--studio-muted)] text-sm">Loading plays…</p>
        </div>
      );
    }
    if (candidatesError || !candidates) {
      return (
        <div
          className="flex flex-col items-center gap-4 py-16"
          data-testid="studio-curation-error"
          role="alert"
        >
          <p className="text-sm text-[var(--studio-muted)]">
            {candidatesError || 'Could not load plays to review.'}
          </p>
          <button
            type="button"
            className="studio-btn-ghost"
            onClick={() => {
              setCandidatesError(null);
              setCandidates(null);
              setPollResumeKey((n) => n + 1);
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    return (
      <CurationReviewPanel
        candidates={candidates}
        deadlineAt={curationDeadline}
        submitting={curateSubmitting}
        error={curateError}
        sourcePreviewUrl={sourcePreviewUrl}
        sourcePreviewExpiresAt={sourcePreviewExpiresAt}
        refreshingPreview={refreshingPreview}
        onRefreshPreview={() => {
          void handleRefreshPreview();
        }}
        initialReelName={dispatchName}
        onSubmit={(ids, name) => {
          void handleCurateSubmit(ids, name);
        }}
      />
    );
  }

  if (flow.kind === 'polling' && pollState.kind === 'polling') {
    return (
      <StudioProgressPanel
        status={pollState.job.status}
        detectWindowsTotal={pollState.job.detect_windows_total}
        detectWindowsDone={pollState.job.detect_windows_done}
        detectWindowsFailed={pollState.job.detect_windows_failed}
        vodDurationSec={
          pollState.job.source_duration_sec ?? flow.vodDurationSec
        }
        overdue={pollState.overdue}
        detectQueued={Boolean(pollState.job.detect_dispatch_deferred)}
        allowLeave
      />
    );
  }

  if (flow.kind === 'polling') {
    return <StudioProgressPanel status="downloading" allowLeave />;
  }

  return (
    <div
      className="flex flex-col items-center gap-4 py-16"
      data-testid="studio-generate-starting"
    >
      <div className="studio-spinner" />
      <p className="text-[var(--studio-muted)] text-sm">
        {flow.kind === 'acquiring' ? 'Cueing the tape…' : 'Checking your allotment…'}
      </p>
    </div>
  );
};

export default StudioGeneratePage;
